import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutGrid,
  Users,
  Target,
  Activity,
  Facebook,
  Linkedin,
  Search,
  FileWarning,
  LogOut,
  CalendarDays,
  MapPin,
  MessageSquare,
  ShoppingBag,
  Sparkles,
  Megaphone,
} from "lucide-react";

import { cn } from "@/lib/cn";
import { useCurrentUser } from "@/lib/api";

interface NavBadge {
  count?: number;
  label?: string;
  variant?: "blue" | "green" | "yellow" | "rose" | "indigo" | "amber" | "slate";
}

interface NavItemConfig {
  to: string;
  label: string;
  icon: React.ElementType;
  badge?: NavBadge;
}

interface SidebarProps {
  badges: { mql?: number; unidentified?: number };
  onItemClick?: () => void;
}

const AVATAR_COLORS = [
  "#3b82f6", "#06b6d4", "#14b8a6", "#22c55e",
  "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899",
];

function UserAvatar({ name }: { name: string }) {
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
  const color = AVATAR_COLORS[
    initials.charCodeAt(0) % AVATAR_COLORS.length
  ];
  return (
    <div
      className="h-7 w-7 rounded-full shrink-0 flex items-center justify-center text-[10px] font-semibold text-white select-none"
      style={{ backgroundColor: color }}
    >
      {initials}
    </div>
  );
}

export function Sidebar({ badges, onItemClick }: SidebarProps) {
  const { data: user } = useCurrentUser();
  const { search } = useLocation();
  // Two groups. Everything under REPORTING is served by the monthly marketing
  // pipeline; PRODUCT reads a different system entirely (the product-analytics
  // service), on its own date range. Mixing them in one list implied a shared
  // refresh cycle that does not exist.
  const items: NavItemConfig[] = [
    { to: "/", label: "Overview", icon: LayoutGrid },
    { to: "/comparison", label: "Historical Comparison", icon: CalendarDays },
    { to: "/leads", label: "Lead Summary", icon: Users, badge: { count: badges.mql } },
    { to: "/nearby-campaigns", label: "Nearby Campaigns", icon: MapPin },
    { to: "/ai-website-builder", label: "AI Website Builder", icon: Sparkles },
    { to: "/goals", label: "Goal Tracking", icon: Target },
    { to: "/efficiency", label: "Channel Efficiency", icon: Activity },
    { to: "/meta", label: "Meta Campaigns", icon: Facebook },
    { to: "/linkedin-ads", label: "LinkedIn Campaigns", icon: Linkedin },
    { to: "/google-ads", label: "Google Ads", icon: Search },
    { to: "/openai-ads", label: "OpenAI Ads", icon: MessageSquare },
    {
      to: "/unidentified",
      label: "Unidentified Leads",
      icon: FileWarning,
      badge: { count: badges.unidentified },
    },
    { to: "/marketing-updates", label: "Marketing Updates", icon: Megaphone },
  ];

  const productItems: NavItemConfig[] = [
    { to: "/webstore-analytics", label: "Webstore Analytics", icon: ShoppingBag },
  ];

  const name = user?.name || user?.email || "";

  return (
    <aside className="flex w-full md:w-[240px] md:shrink-0 bg-sidebar text-sidebar-fg flex-col h-full">
      <div className="px-6 pt-6 pb-5 border-b border-white/5">
        <div className="font-display text-lg font-semibold tracking-tight">Growth Insights</div>
        <div className="text-xs text-sidebar-muted mt-0.5">Marketing Intelligence</div>
      </div>

      <nav className="flex-1 overflow-y-auto py-5">
        <NavSection label="REPORTING" items={items} search={search} onItemClick={onItemClick} />
        <NavSection
          label="PRODUCT"
          items={productItems}
          search={search}
          onItemClick={onItemClick}
          className="mt-6"
        />
      </nav>

      <div className="px-6 py-4 border-t border-white/5 text-xs flex items-center gap-3">
        {user && <UserAvatar name={name} />}
        <div className="flex-1 min-w-0">
          <div className="text-sidebar-fg font-medium truncate">{user?.name || "—"}</div>
          <div className="text-sidebar-muted truncate">{user?.email || ""}</div>
        </div>
        <a href="/auth/logout" title="Sign out" className="text-sidebar-muted hover:text-sidebar-fg transition-colors shrink-0">
          <LogOut className="h-4 w-4" />
        </a>
      </div>
    </aside>
  );
}

interface NavSectionProps {
  label: string;
  items: NavItemConfig[];
  search: string;
  onItemClick?: () => void;
  className?: string;
}

function NavSection({ label, items, search, onItemClick, className }: NavSectionProps) {
  return (
    <div className={className}>
      <div className="px-6 mb-2 text-[10px] tracking-[0.16em] text-sidebar-muted">{label}</div>
      <ul className="space-y-0.5 px-3">
        {items.map((it) => (
          <li key={it.to}>
            <NavLink
              end={it.to === "/"}
              to={{ pathname: it.to, search }}
              onClick={() => onItemClick?.()}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors",
                  isActive
                    ? "bg-accent-blue text-white"
                    : "text-sidebar-fg/85 hover:bg-white/5"
                )
              }
            >
              <it.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
              <span className="flex-1 leading-tight">{it.label}</span>
              {it.badge?.count != null && (
                <span className="text-[10px] font-medium bg-white/10 text-sidebar-fg px-1.5 py-0.5 rounded">
                  {it.badge.count}
                </span>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}
